//@ts-nocheck

import React, { useEffect, useState } from 'react';
import { Input } from '../common';
import SearchDropdown from '../common/SearchDropdown';
import { useAppSelector } from '../../hooks/useAppSelector';
import { Listbox } from '@headlessui/react';
import { ChevronDownIcon } from '../icons';
import { Textarea } from '../common/Textarea/Textarea';

interface Education {
  id: number;
  course: string;
  branch: string;
  startDateMonth: string;
  startDateYear: string;
  endDateMonth: string;
  endDateYear: string;
  university: string;
  location: string;
  grade: string;
  selectedCountry: any;
  currentlyStudying: boolean;
  description: string; // Added description field

}

interface StyledDropdownProps {
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}

const StyledDropdown: React.FC<StyledDropdownProps> = ({ options, value, onChange, placeholder }) => {
  const selectedOption = options.find(opt => opt.value === value);

  return (
    <Listbox value={value} onChange={onChange}>
      <div className="relative">
        <Listbox.Button className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-200 focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2">
          <span className={`block truncate text-[14px] ${!selectedOption ? 'text-gray-400' : 'text-gray-900'}`}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </span>
        </Listbox.Button>
        <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none">
          {options.map((option) => (
            <Listbox.Option
              key={option.value}
              value={option.value}
              className={({ active }) =>
                `relative cursor-pointer select-none py-2 px-4 ${active ? 'bg-gray-100' : 'text-gray-900'}`
              }
            >
              {({ selected }) => (
                <span className={`block truncate text-[14px] ${selected ? 'font-medium' : 'font-normal'}`}>
                  {option.label}
                </span>
              )}
            </Listbox.Option>
          ))}
        </Listbox.Options>
      </div>
    </Listbox>
  );
};

interface DateSelectorProps {
  selectedMonth: string;
  selectedYear: string;
  onMonthChange: (value: string) => void;
  onYearChange: (value: string) => void;
  label: string;
  disabled?: boolean;
}

const DateSelector: React.FC<DateSelectorProps> = ({
  selectedMonth,
  selectedYear,
  onMonthChange,
  onYearChange,
  label,
  disabled = false
}) => {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1950 + 1 }, (_, i) => ({
    value: (currentYear - i).toString(),
    label: (currentYear - i).toString()
  }));

  const months = Array.from({ length: 12 }, (_, i) => ({
    value: (i + 1).toString().padStart(2, '0'),
    label: new Date(2000, i).toLocaleString('default', { month: 'long' })
  }));

  return (
    <div className=" space-y-1 md:space-y-2 lg:space-y-2">
      <label className="wg-label block capitalize shrink-0 items-center gap-1 text-[13px] md:text-[14px] lg:text-[14px] text-[#2f2f31] wg-antialiased leading-4 md:leading-5 lg:leading-5 font-medium">{label}</label>
      <div className="grid grid-cols-2 gap-2">
        <StyledDropdown
          options={months}
          value={selectedMonth}
          onChange={onMonthChange}
          placeholder="Month"
        />
        <StyledDropdown
          options={years}
          value={selectedYear}
          onChange={onYearChange}
          placeholder="Year"
        />
      </div>
    </div>
  );
};

// Helper function to convert text with bullet points
const formatDescriptionWithBullets = (text) => {
  if (!text) return [];
  
  // Split by new lines or bullet point markers
  const lines = text.split(/\n|•/).map(line => line.trim()).filter(line => line);
  
  // Convert each line to a bullet point if it doesn't already start with one
  return lines.map(line => line.startsWith('•') ? line : `• ${line}`);
};

// Helper function to parse description text and detect if it contains bullet points
const parseDescription = (text) => {
  if (!text) return { hasListFormat: false, text };
  
  // Check if text contains bullet points or numbered lists
  const hasListFormat = /(\n|\r)[-•*]|^\s*[-•*]|\d+\.\s/.test(text);
  
  return { hasListFormat, text };
};

interface EducationDetailsFormProps {
  value: any;
  onChange: (e: { target: { name: string; value: any } }) => void;
  initialData?: any[];
  required?: string;
  error?: string;
}

const EducationDetailsForm: React.FC<EducationDetailsFormProps> = ({ value, onChange, initialData, required, error }) => {
  const [educations, setEducations] = useState<Education[]>([]);
  const [showForm, setShowForm] = useState(false);
  const locations = useAppSelector((state) => state?.jobs?.locationDetails);
  const [loadingLocation, setLoadingLocation] = useState(false);

  useEffect(() => {
    if (initialData && initialData.length > 0) {
      const formattedEducations = initialData.map(edu => ({
        id: Date.now() + Math.random(),
        course: edu.degree || '',
        branch: edu.specialization || '',
        startDateMonth: edu.startDateMonth || '',
        startDateYear: edu.startDateYear || '',
        endDateMonth: edu.endDateMonth || '',
        endDateYear: edu.endDateYear || '',
        university: edu.university || '',
        location: edu.location || '',
        grade: edu.grade || '',
        selectedCountry: findLocationFromString(edu.location),
        currentlyStudying: edu.end_date === 'Present',
        description: edu.description || '' // Added description field
      }));      

      setEducations(formattedEducations);
      setShowForm(true);
      const formattedData = formatEducationsForParent(formattedEducations);
      onChange({ target: { name: 'education_details', value: formattedData } });
    }
  }, [initialData]);

  const findLocationFromString = (locationStr: string) => {
    if (!locationStr || !locations) return null;
    return locations.find(loc =>
      loc.name.toLowerCase().includes(locationStr.toLowerCase()) ||
      locationStr.toLowerCase().includes(loc.name.toLowerCase())
    );
  };

  const handleAddEducation = () => {
    if (educations.length >= 10) return;

    const newEducation: Education = {
      id: Date.now(),
      course: '',
      branch: '',
      startDateMonth: '',
      startDateYear: '',
      endDateMonth: '',
      endDateYear: '',
      university: '',
      location: '',
      grade: '',
      selectedCountry: null,
      currentlyStudying: false,
      description: '' // Added description field
    };

    if (!showForm) {
      setShowForm(true);
      setEducations([newEducation]);
    } else {
      setEducations([...educations, newEducation]);
    }
  };

  const handleDeleteEducation = (id: number) => {
    const updatedEducations = educations.filter(edu => edu.id !== id);
    if (updatedEducations.length === 0) {
      setShowForm(false);
    }
    setEducations(updatedEducations);
    const formattedData = formatEducationsForParent(updatedEducations);
    onChange({ target: { name: 'education_details', value: formattedData } });
  };

  const handleEducationChange = (id: number, field: keyof Education, value: any) => {
    const updatedEducations = educations.map(edu => {
      if (edu.id === id) {
        return { ...edu, [field]: value };
      }
      return edu;
    });
    setEducations(updatedEducations);
    const formattedData = formatEducationsForParent(updatedEducations);
    onChange({ target: { name: 'education_details_status', value: formattedData } });
  };


  const formatEducationsForParent = (edus: Education[]) => {
    return edus.map(edu => ({
      course: edu.course,
      branch: edu.branch,
      start_date: edu.startDateMonth && edu.startDateYear ? `${edu.startDateMonth}/${edu.startDateYear}` : '',
      end_date: edu.currentlyStudying ? 'Present' : (edu.endDateMonth && edu.endDateYear ? `${edu.endDateMonth}/${edu.endDateYear}` : ''),
      university: edu.university,
      location: edu.selectedCountry?.name || edu.location,
      grade: edu.grade,
      description: edu.description,
    }));
  };

  const handleCountryChange = (id: number) => (country: any) => {
    handleEducationChange(id, 'selectedCountry', country);
  };

    // Function to render description content with or without bullet points
    const renderDescription = (description) => {
      const { hasListFormat, text } = parseDescription(description);
      
      if (hasListFormat) {
        const bulletPoints = formatDescriptionWithBullets(text);
        return (
          <ul className="list-disc pl-5 space-y-1 text-sm">
            {bulletPoints.map((point, idx) => (
              <li key={idx}>{point.replace(/^•\s*/, '')}</li>
            ))}
          </ul>
        );
      }
      
      return <p className="text-sm">{text}</p>;
    };
  

  return (
    <div className="space-y-4">
      {showForm && educations.map((education) => (
        <div key={education.id} className="bg-gray-100 p-4 rounded-md">
          <div className="flex justify-end w-full">
            <button
              type="button"
              onClick={() => handleDeleteEducation(education.id)}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={24}
                height={24}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-4 h-4"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M4 7l16 0" />
                <path d="M10 11l0 6" />
                <path d="M14 11l0 6" />
                <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
                <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Course"
              value={education.course}
              onChange={(e) => handleEducationChange(education.id, 'course', e.target.value)}
              placeholder="e.g., B.Tech, M.Tech, MBA"
            />

            <Input
              label="Branch/ Specialization"
              value={education.branch}
              onChange={(e) => handleEducationChange(education.id, 'branch', e.target.value)}
              placeholder="e.g., Computer Science, Electronics"
            />

            <DateSelector
              label="Start of Course"
              selectedMonth={education.startDateMonth}
              selectedYear={education.startDateYear}
              onMonthChange={(value) => handleEducationChange(education.id, 'startDateMonth', value)}
              onYearChange={(value) => handleEducationChange(education.id, 'startDateYear', value)}
            />

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="wg-label capitalize inline-flex shrink-0 items-center gap-1 text-[13px] md:text-[14px] lg:text-[14px] text-[#2f2f31] wg-antialiased leading-4 md:leading-5 lg:leading-5 font-medium">End of Course</label>
                {/* <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={education.currentlyStudying}
                    onChange={(e) => handleEducationChange(education.id, 'currentlyStudying', e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-500">Currently Studying</span>
                </div> */}
              </div>
              
              <DateSelector
                label=""
                selectedMonth={education.endDateMonth}
                selectedYear={education.endDateYear}
                onMonthChange={(value) => handleEducationChange(education.id, 'endDateMonth', value)}
                onYearChange={(value) => handleEducationChange(education.id, 'endDateYear', value)}
                disabled={education.currentlyStudying}
              />
            </div>

            <Input
              label="University/ College"
              value={education.university}
              onChange={(e) => handleEducationChange(education.id, 'university', e.target.value)}
              placeholder="Enter university/college name"
            />

            <div className="flex flex-col gap-y-[8px] md:gap-y-2 lg:gap-y-2">
              <label className="wg-label capitalize inline-flex shrink-0 items-center gap-1 text-[13px] md:text-[14px] lg:text-[14px] text-[#2f2f31] wg-antialiased leading-4 md:leading-5 lg:leading-5 font-medium">Location</label>
              <SearchDropdown
                items={locations}
                buttonText="Search by city, state, or country"
                onChange={handleCountryChange(education.id)}
                selectedItem={education.selectedCountry}
                isLoading={loadingLocation}
              />
            </div>

            <Input
              label="Grade/Score"
              value={education.grade}
              onChange={(e) => handleEducationChange(education.id, 'grade', e.target.value)}
              placeholder="e.g., 8.5 CGPA or 85%"
            />
          </div>
          <div className="mt-4">
            <label className="wg-label capitalize inline-flex shrink-0 items-center gap-1 text-[13px] md:text-[14px] lg:text-[14px] text-[#2f2f31] wg-antialiased leading-4 md:leading-5 lg:leading-5 font-medium">
              Description
            </label>
            <div className="mt-1">
              <Textarea
                placeholder="Describe your education details"
                value={education.description}
                onChange={(e) => handleEducationChange(education.id, 'description', e.target.value)}
                rows={4}
              />
              {/* <div className="mt-2 text-xs text-gray-500">
                Use bullet points (• or -) at the beginning of each line for listing achievements or coursework
              </div> */}
              
              {/* Preview of description with formatted bullet points if available */}
              {/* {education.description && (
                <div className="mt-3 bg-white p-3 rounded border border-gray-200">
                  <div className="text-xs font-medium text-gray-500 mb-1">Preview:</div>
                  {renderDescription(education.description)}
                </div>
              )} */}
            </div>
          </div>
        </div>
        
      ))}

      <button
        onClick={handleAddEducation}
        type="button"
        className="px-0 py-2 text-sm font-semibold text-color"
      >
        + Add Education Details
      </button>
      {error && (
        <p className="mt-1 text-sm text-red-500">
          {error}
        </p>
      )}
    </div>
  );
};

export default EducationDetailsForm;